<script setup lang="ts">
/* The one input for talking about a policy, a row or a node. A comment by
   default; a mode switch turns the same draft into a pinned note (rich text)
   or, on a design row, a tracked question for scoping. `@` opens the mention
   menu in comment mode, and each pick travels beside the body as its id (a
   lower-cased email), so a thread knows who was tagged without parsing prose.
   Enter sends a comment (unless the menu is open, where it picks), Shift+Enter
   breaks the line; a note takes Enter as a line break and sends on ⌘Enter. The
   draft is cleared only once the send succeeded, so nothing typed is lost. */
import { POLICY_ROLES } from '@worker/policy-builder/client'
import { Extension } from '@tiptap/vue-3'
import type { Editor } from '@tiptap/vue-3'
import type { PolicyRoom } from '~/composables/usePolicyRoom'
import { mentionsInDoc, type ComposerMode } from '~/utils/policy-studio/conversation'

const {
  room,
  itemId,
  parentId,
  answerTo,
  modes = ['comment'],
  placeholder = 'Leave a comment…',
  submitLabel = 'Comment',
} = defineProps<{
  room: PolicyRoom
  itemId: string
  /** Makes a comment a reply; replies are comments only. */
  parentId?: string
  /** The question id `answer` mode answers. */
  answerTo?: string
  modes?: ComposerMode[]
  /** Comment mode's hint. */
  placeholder?: string
  /** Comment mode's send button. */
  submitLabel?: string
}>()

const emit = defineEmits<{ sent: [kind: ComposerMode] }>()

const MODES = {
  comment: {
    label: 'Comment',
    icon: 'i-ph-chat-circle',
    tip: 'A conversation: reply, tag people, resolve when done',
  },
  note: {
    label: 'Note',
    icon: 'i-ph-note',
    tip: 'Pinned for whoever reads this next. Rich text; only you can edit it',
    placeholder: 'Add a note for everyone on this policy',
    submitLabel: 'Add note',
  },
  ask: {
    label: 'Ask scoping',
    icon: 'i-ph-question',
    tip: "A tracked question that waits on scoping's answer",
    placeholder: 'Ask scoping about this answer…',
    submitLabel: 'Send to scoping',
  },
  answer: {
    label: 'Answer',
    icon: 'i-ph-arrow-bend-up-left',
    tip: 'Send your answer back to design',
    placeholder: 'Write your answer…',
    submitLabel: 'Send answer',
  },
} satisfies Record<
  ComposerMode,
  {
    label: string
    icon: string
    tip: string
    placeholder?: string
    submitLabel?: string
  }
>

const mode = ref<ComposerMode>(modes[0] ?? 'comment')
watch(
  () => modes,
  (next) => {
    if (!next.includes(mode.value)) mode.value = next[0] ?? 'comment'
  },
)
const hint = computed(() =>
  mode.value === 'comment' ? placeholder : MODES[mode.value].placeholder,
)
const action = computed(() =>
  mode.value === 'comment' ? submitLabel : MODES[mode.value].submitLabel,
)

const toast = useToast()
const { attempt } = useStudioAction()
const mentionables = usePolicyMentionables(room)
const draft = ref('')
const busy = ref(false)
const composer = useTemplateRef('composer')
const noteEditor = useTemplateRef('noteEditor')

const mentionsOf = (editor: Editor): string[] => mentionsInDoc(editor.state.doc)

const deliver = async (
  kind: ComposerMode,
  body: string,
  editor?: Editor,
): Promise<boolean> => {
  if (kind === 'comment') {
    const ok = room.comment(
      itemId,
      body,
      editor ? mentionsOf(editor) : [],
      parentId,
    )
    if (!ok)
      toast.add({
        title: 'Not sent',
        description: 'Not connected — try again once reconnected.',
        color: 'error',
      })
    return ok
  }
  if (kind === 'answer') {
    const ok = await attempt(() => room.answer(answerTo!, body), 'Answer not sent')
    if (ok) toast.add({ title: 'Answer sent', color: 'success' })
    return ok
  }
  /* A question goes to the team it tags (S-N22), scoping when it tags none. */
  const tagged = editor ? mentionsOf(editor) : []
  const team =
    POLICY_ROLES.find((role) => tagged.includes(`team:${role}`)) ?? 'scoping'
  const ok = await attempt(
    () => {
      if (kind === 'note') return room.addNote(itemId, body)
      return room.ask(itemId, body, {
        to: team,
        mentions: tagged.length ? tagged : ['team:scoping'],
      })
    },
    kind === 'note' ? 'Note not added' : 'Question not sent',
  )
  if (ok && kind === 'ask') toast.add({ title: `Sent to ${team}`, color: 'success' })
  return ok
}

const submit = async (): Promise<void> => {
  if (!room.canDiscuss.value || busy.value) return
  const kind = mode.value
  const editor =
    kind === 'note' ? undefined : (composer.value?.editor as Editor | undefined)
  const body =
    kind === 'note'
      ? draft.value.trim()
      : editor?.getText({ blockSeparator: '\n' }).trim()
  if (!body) return
  busy.value = true
  try {
    if (!(await deliver(kind, body, editor))) return
  } finally {
    busy.value = false
  }
  editor?.commands.clearContent()
  draft.value = ''
  emit('sent', kind)
}

defineExpose({
  focus: async (kind?: ComposerMode) => {
    if (kind && modes.includes(kind)) mode.value = kind
    await nextTick()
    if (mode.value === 'note') noteEditor.value?.focus()
    else composer.value?.editor?.commands.focus('end')
  },
})

const sendOnEnter = (send: () => void) => [
  Extension.create({
    name: 'commentSubmit',
    priority: 1000,
    addKeyboardShortcuts: () => ({ Enter: () => (send(), true) }),
  }),
]

/* The note editor reports typing on a debounce but its value at once on blur,
   so blurring first hands the shortcut the whole note. Capture keeps the key
   from reaching the editor, whose own Mod-Enter is a line break. */
const submitOnModEnter = (event: KeyboardEvent) => {
  ;(event.target as HTMLElement).blur()
  void submit()
}

/* Lifts the menu out of the card that clips it; inside an open dialog it stays
   in the dialog, whose focus trap would otherwise swallow clicks on it. */
const menuHost = (editor: Editor) => () =>
  editor.view.dom.closest<HTMLElement>('[role="dialog"]') ?? document.body
</script>

<template>
  <UChatPrompt
    v-model="draft"
    class="gap-0 p-0"
    :disabled="!room.canDiscuss.value"
    :placeholder="hint"
    :ui="{ footer: 'flex items-center gap-2 px-2 pb-2' }"
    variant="subtle"
    @submit="submit"
  >
    <template #body="{ submit: send, placeholder: text }">
      <div
        v-if="mode === 'note'"
        class="text-[13px]"
        @keydown.meta.enter.capture.stop.prevent="submitOnModEnter"
      >
        <MarkdownEditor
          ref="noteEditor"
          :model-value="draft"
          :placeholder="text"
          aria-label="Add a note"
          compact
          live
          @markdown="draft = $event"
        />
      </div>
      <!-- Keyed by mode: the editor reads its placeholder once, at creation. -->
      <UEditor
        v-else
        ref="composer"
        :key="mode"
        v-slot="{ editor }"
        v-model="draft"
        class="min-h-10 w-full text-sm"
        :extensions="sendOnEnter(send)"
        :placeholder="text"
        :ui="{
          base: 'px-3! pt-2.5! [&_.mention]:bg-primary/10 [&_.mention]:text-primary [&_.mention]:rounded-sm [&_.mention]:px-0.5',
        }"
        content-type="markdown"
      >
        <UEditorMentionMenu
          v-if="mode === 'comment'"
          :append-to="menuHost(editor)"
          :editor="editor"
          :items="mentionables"
        />
      </UEditor>
    </template>
    <template #footer>
      <UFieldGroup v-if="modes.length > 1" size="xs">
        <UTooltip
          v-for="option in modes"
          :key="option"
          :text="MODES[option].tip"
        >
          <UButton
            :aria-pressed="mode === option"
            :color="mode === option ? 'primary' : 'neutral'"
            :icon="MODES[option].icon"
            :label="MODES[option].label"
            :variant="mode === option ? 'soft' : 'ghost'"
            :data-testid="`composer-mode-${option}`"
            @click="mode = option"
          />
        </UTooltip>
      </UFieldGroup>
      <UChatPromptSubmit
        class="ml-auto rounded-full"
        :aria-label="action"
        :disabled="!room.canDiscuss.value || busy || !draft.trim()"
        color="neutral"
        size="sm"
        variant="soft"
        data-testid="composer-submit"
      />
    </template>
  </UChatPrompt>
</template>
